/* eslint-disable react-refresh/only-export-components */
import { lazy, Suspense, useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  useRouteError
} from 'react-router';
import { BaseLayout } from '@triplabo/web/layouts';
import { isLoggedIn } from '@triplabo/web/services';
import { loadZendesk, unloadZendesk } from '@triplabo/web/utils';

const Home = lazy(() => import('@triplabo/web/views/Home'));
const Login = lazy(() => import('@triplabo/web/views/Login'));
const PassportInfo = lazy(() => import('@triplabo/web/views/PassportInfo'));
const Passports = lazy(() => import('@triplabo/web/views/Passports'));
const Permit = lazy(() => import('@triplabo/web/views/ResidenceCert'));
const UploadResult = lazy(() => import('@triplabo/web/views/UploadResult'));
const StatusDetail = lazy(() => import('@triplabo/web/views/StatusDetail'));
const NotFound = lazy(() => import('@triplabo/web/views/NotFound'));

function RootErrorBoundary() {
  const error = useRouteError() as Error;
  if (error.message.includes('Failed to fetch dynamically imported module')) {
    location.reload();
  }
  return null;
}

const lazyView = (view: JSX.Element) => {
  return <Suspense fallback="Loading...">{view}</Suspense>;
};

const RequireAuth = () => {
  const loggedIn = isLoggedIn();

  useEffect(() => {
    if (!loggedIn) return;
    loadZendesk();
    return unloadZendesk;
  }, [loggedIn]);

  if (!loggedIn) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route errorElement={<RootErrorBoundary />}>
      <Route>
        <Route path="/" element={<BaseLayout />}>
          <Route path="login" element={lazyView(<Login />)} />
          <Route path="login/email" element={lazyView(<Login />)} />
          <Route path=":locale/login" element={lazyView(<Login />)} />
          <Route path="permit" element={lazyView(<Permit />)} />
          <Route
            path="permit/:publicId/:signature"
            element={lazyView(<Permit />)}
          />
          <Route
            path=":locale/permit/:publicId/:signature"
            element={lazyView(<Permit />)}
          />
          <Route path="uploadresult" element={lazyView(<UploadResult />)} />
          <Route element={<RequireAuth />}>
            <Route index element={<Navigate to="passports" replace />} />
            <Route path="home" element={lazyView(<Home />)} />
            <Route path="passports" element={lazyView(<Passports />)} />
            <Route path="passports/info" element={lazyView(<PassportInfo />)} />
            <Route
              path="detail/:orderId"
              element={lazyView(<StatusDetail />)}
            />
            <Route
              path="status/:orderId"
              element={lazyView(<StatusDetail />)}
            />
          </Route>
          <Route path="*" element={lazyView(<NotFound />)} />
        </Route>
      </Route>
    </Route>
  )
);

export default router;
